"use client";

import { useRouter } from "@tanstack/react-router";
import { ArrowRightIcon, LaptopIcon, MoonIcon, SunIcon } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import type { NavigationGroup, NavigationItem } from "@/components/admin/AppSidebar/types";
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import { useSearch } from "@/providers/search-provider";
import { useTheme } from "@/providers/theme-provider";
import { DialogDescription, DialogTitle } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { navigation, paths } from "./AppSidebar/config";

const NavSubCommandItem = ({
	subItem,
	runCommand,
}: {
	subItem: Omit<NavigationItem, "items">;
	runCommand: (command: () => unknown) => void;
}) => {
	const { t } = useTranslation();
	const router = useRouter();
	const path = paths[subItem.module];

	return (
		<CommandItem
			keywords={[t(($) => $.common.components.sidebar.items[subItem.module])]}
			onSelect={() => runCommand(() => router.navigate({ to: path }))}
			value={subItem.module}
		>
			<ArrowRightIcon className="mr-2 size-4" />
			{t(($) => $.common.components.sidebar.items[subItem.module])}
		</CommandItem>
	);
};

const NavCommandItem = ({
	item,
	runCommand,
}: {
	item: NavigationItem;
	runCommand: (command: () => unknown) => void;
}) => {
	const { t } = useTranslation();
	const router = useRouter();
	const Icon = item.icon;

	const path = paths[item.module];

	const parentItem = (
		<CommandItem
			keywords={[t(($) => $.common.components.sidebar.items[item.module])]}
			onSelect={() => runCommand(() => router.navigate({ to: path }))}
			value={item.module}
		>
			{Icon && <Icon className="mr-2 size-4" />}
			{t(($) => $.common.components.sidebar.items[item.module])}
		</CommandItem>
	);

	if (!item.items) {
		return parentItem;
	}

	return (
		<>
			{parentItem}
			{item.items.map((subItem) => (
				<NavSubCommandItem key={subItem.module} runCommand={runCommand} subItem={subItem} />
			))}
		</>
	);
};

const NavGroupCommands = ({
	group,
	runCommand,
}: {
	group: NavigationGroup;
	runCommand: (command: () => unknown) => void;
}) => {
	const { t } = useTranslation();

	return (
		<CommandGroup heading={t(($) => $.common.components.sidebar.groups[group.title])}>
			{group.items.map((item) => (
				<NavCommandItem item={item} key={item.module} runCommand={runCommand} />
			))}
		</CommandGroup>
	);
};

const ThemeCommands = ({ runCommand }: { runCommand: (command: () => unknown) => void }) => {
	const { setTheme } = useTheme();
	const { t } = useTranslation();

	return (
		<CommandGroup heading={t(($) => $.common.components.commandMenu.theme.title)}>
			<CommandItem onSelect={() => runCommand(() => setTheme("light"))}>
				<SunIcon className="mr-2 size-4" />
				<span>{t(($) => $.common.components.commandMenu.theme.options.light)}</span>
			</CommandItem>
			<CommandItem onSelect={() => runCommand(() => setTheme("dark"))}>
				<MoonIcon className="mr-2 size-4" />
				<span>{t(($) => $.common.components.commandMenu.theme.options.dark)}</span>
			</CommandItem>
			<CommandItem onSelect={() => runCommand(() => setTheme("system"))}>
				<LaptopIcon className="mr-2 size-4" />
				<span>{t(($) => $.common.components.commandMenu.theme.options.system)}</span>
			</CommandItem>
		</CommandGroup>
	);
};

// --- Main Component ---

export function CommandMenu() {
	const { open, setOpen } = useSearch();
	const { t } = useTranslation();

	const runCommand = React.useCallback(
		(command: () => unknown) => {
			setOpen(false);
			command();
		},
		[setOpen]
	);

	return (
		<CommandDialog onOpenChange={setOpen} open={open}>
			<DialogTitle className="sr-only">{t(($) => $.common.components.commandMenu.search.title)}</DialogTitle>
			<DialogDescription className="sr-only">
				{t(($) => $.common.components.commandMenu.search.description)}
			</DialogDescription>
			<CommandInput placeholder={t(($) => $.common.components.commandMenu.search.placeholder)} />
			<CommandList>
				<ScrollArea className="h-75">
					<CommandEmpty>{t(($) => $.common.components.commandMenu.search.empty)}</CommandEmpty>
					{navigation.groups.map((group) => (
						<NavGroupCommands group={group} key={group.title} runCommand={runCommand} />
					))}
					<CommandSeparator />
					<ThemeCommands runCommand={runCommand} />
				</ScrollArea>
			</CommandList>
		</CommandDialog>
	);
}
